import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingBag, TrendingDown, Send } from "lucide-react";
import { toast } from "sonner";

const clean = (n) => (n || "").replace(/\*/g, " · ");

export function UpsellSalesCard() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/guest-portal/upsell-sales?month=${month}`).then((r) => setData(r.data)).catch(() => {}); }, [month]);
  return (
    <section className="ns-card dash-wash dash-teal p-6" data-testid="upsell-sales-card">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-2xl flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Upsell sales</div>
          <p className="text-xs text-slate-500">Which extras sell best at each property.</p>
        </div>
        <div className="flex items-end gap-3">
          <span className="text-sm font-serif" data-testid="upsell-sales-total">£{(data?.total_revenue || 0).toFixed(2)}</span>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40 bg-white" data-testid="upsell-sales-month" />
        </div>
      </div>
      {data && !data.properties.length && <div className="text-sm text-slate-400 italic" data-testid="upsell-sales-empty">No extras sold in {month}.</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {data?.properties.map((p) => (
          <div key={p.property_id} className="rounded-xl border border-slate-100 p-4" data-testid={`upsell-sales-property-${p.property_id}`}>
            <div className="flex justify-between text-sm mb-2"><span className="font-medium truncate pr-2">{clean(p.property_name)}</span><span className="font-mono">£{p.revenue.toFixed(2)}</span></div>
            {p.items.map((i, idx) => (
              <div key={i.name} className="flex justify-between text-xs py-0.5">
                <span className={idx === 0 ? "text-teal-800 font-medium" : "text-slate-600"}>{idx === 0 ? "★ " : ""}{i.name} × {i.count}</span>
                <span className="font-mono text-slate-600">£{i.revenue.toFixed(2)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

export function QuietOffersCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/offers/quiet?days=60").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <section className="ns-card dash-wash dash-sun p-6" data-testid="quiet-offers-card">
      <div className="font-serif text-2xl flex items-center gap-2"><TrendingDown className="w-5 h-5" /> Quiet month offers</div>
      <p className="text-xs text-slate-500 mb-4">Properties under 50% booked over the next 60 days, with a suggested discount for past guests.</p>
      {data && !data.rows.length && <div className="text-sm text-slate-400 italic">Every property is at least 50% booked — nice.</div>}
      <div className="space-y-2">
        {data?.rows.map((r) => (
          <div key={r.property_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3" data-testid={`quiet-offer-row-${r.property_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{clean(r.property_name)}</div>
              <div className="text-[11px] text-slate-500">{r.occupancy_pct}% booked · {r.recipients} past guests{r.last_sent_at ? ` · last offer ${r.last_sent_at.slice(0, 10)}` : ""}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">Suggest {r.suggested_discount}% off</span>
              <Button size="sm" className="rounded-full ns-btn-primary" disabled={!r.recipients} onClick={() => setOpen(r)} data-testid={`quiet-offer-open-${r.property_id}`}>Review email</Button>
            </div>
          </div>
        ))}
      </div>
      {open && <OfferDialog row={open} onClose={() => { setOpen(null); load(); }} />}
    </section>
  );
}

function OfferDialog({ row, onClose }) {
  const name = clean(row.property_name).split(" · ")[0];
  const [pct, setPct] = useState(row.suggested_discount);
  const [subject, setSubject] = useState(`A little thank-you: ${row.suggested_discount}% off ${name}`);
  const [message, setMessage] = useState(`Thanks again for staying with us at ${name}. We have some lovely dates free over the next couple of months and wanted to offer past guests first pick.`);
  const [test, setTest] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (isTest) => {
    if (!isTest && !window.confirm(`Email ${row.recipients} past guests?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/offers/quiet/${row.property_id}/send`, { discount_pct: Number(pct), subject, message, test_email: isTest ? test : null });
      toast.success(isTest ? `Test sent to ${test}` : `Sending to ${r.data.queued} guests`);
      if (!isTest) onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="quiet-offer-dialog">
        <DialogHeader><DialogTitle className="font-serif">Offer for {name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[100px_1fr] gap-2 items-center text-xs">
            <span className="text-slate-500">Discount %</span>
            <Input type="number" min={1} max={50} value={pct} onChange={(e) => setPct(e.target.value)} className="h-9" data-testid="quiet-offer-pct" />
            <span className="text-slate-500">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" data-testid="quiet-offer-subject" />
          </div>
          <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="quiet-offer-message" />
          <p className="text-[11px] text-slate-500">{row.booking_link ? `Includes a "Book direct" button → ${row.booking_link}` : "No direct booking page for this property — guests are asked to reply with their dates."}</p>
          <div className="flex gap-2">
            <Input placeholder="you@email.com" value={test} onChange={(e) => setTest(e.target.value)} className="h-9" data-testid="quiet-offer-test-email" />
            <Button variant="outline" size="sm" className="rounded-full h-9" disabled={!test || busy} onClick={() => send(true)} data-testid="quiet-offer-test-send">Send test</Button>
          </div>
          <Button className="w-full rounded-full ns-btn-primary" disabled={busy} onClick={() => send(false)} data-testid="quiet-offer-send">
            <Send className="w-4 h-4 mr-2" /> Send to {row.recipients} past guests
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
